import React, {useState} from 'react';
import {makeStyles, withStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import {Backdrop, CircularProgress, Grid} from "@material-ui/core";
import InputAdornment from "@material-ui/core/InputAdornment";
import TodayIcon from "@material-ui/icons/Today";
import InsertInvitationIcon from "@material-ui/icons/InsertInvitation";
import {Alert} from "@material-ui/lab";
import {TextValidator, ValidatorForm} from 'react-material-ui-form-validator';
import {DatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import deLocale from "date-fns/locale/de";
import trLocale from "date-fns/locale/tr";
import ruLocale from "date-fns/locale/ru";
import frLocale from "date-fns/locale/fr";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import ButtonAtm from "../../../../../atomic/Button/Button";
import ColAtm from "../../../../../atomic/Col/Col";
import {getAppLang, isDefined} from "../../../../../../utils";
import {bookingUpdateDates, clearBookingUpdateDates} from "../../../../../../redux/actions/General/BookingUpdateDates/bookingUpdateDatesActions";
import {getReservationDetail} from "../../../../../../redux/actions/Network/Reservation/reservationDetail.actions";

const localeMap = {
    es: esLocale,
    de: deLocale,
    tr: trLocale,
    ru: ruLocale,
    fr: frLocale,
};

const styles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(styles)(props => {
    const {children, classes, onClose, ...other} = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon className="close-button"/>
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {
        padding: theme.spacing(2),
    },
}))(MuiDialogContent);

const useStyles = makeStyles((theme) => ({
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: '#d71f26',
    },
    '@keyframes successPop': {
        '0%': {transform: 'scale(0)', opacity: 0},
        '60%': {transform: 'scale(1.15)', opacity: 1},
        '100%': {transform: 'scale(1)'},
    },
    '@keyframes successRing': {
        '0%': {transform: 'scale(0.75)', opacity: 0.55},
        '100%': {transform: 'scale(1.7)', opacity: 0},
    },
    '@keyframes successRise': {
        '0%': {transform: 'translateY(8px)', opacity: 0},
        '100%': {transform: 'translateY(0)', opacity: 1},
    },
    successBadge: {
        animation: '$successPop 480ms cubic-bezier(0.17, 0.67, 0.35, 1.3) both',
    },
    successRing: {
        animation: '$successRing 1400ms ease-out infinite',
    },
    successBody: {
        animation: '$successRise 420ms ease-out 120ms both',
    },
}));

const toApiDate = (date) => moment(date).format('YYYY-MM-DD');

const BookingUpdateDatesModal = (props) => {

    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {reservation} = props;

    const updateData = useSelector(state => state.bookingUpdateDatesReducer.data);
    const updateError = useSelector(state => state.bookingUpdateDatesReducer.error);
    const updateFetching = useSelector(state => state.bookingUpdateDatesReducer.fetching);

    const [open, setOpen] = useState(false);
    const [checkinDate, setCheckinDate] = useState(moment(reservation.checkin_date).toDate());
    const [checkoutDate, setCheckoutDate] = useState(moment(reservation.checkout_date).toDate());
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [total, setTotal] = useState(reservation.total);

    const nights = moment(checkoutDate).startOf('day').diff(moment(checkinDate).startOf('day'), 'days');
    const perNight = nights > 0 && Number(total) >= 0 ? (Number(total) / nights) : 0;
    const isSuccess = isDefined(updateData) && updateData.status === 200 && updateData.error === false;

    const resetForm = () => {
        setCheckinDate(moment(reservation.checkin_date).toDate());
        setCheckoutDate(moment(reservation.checkout_date).toDate());
        setTotal(reservation.total);
    };

    const handleClickOpen = () => {
        dispatch(clearBookingUpdateDates());
        resetForm();
        setCheckoutOpen(false);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        // Başarılı güncelleme sonrası kullanıcı modalı kapatınca detayları tazele
        if (isSuccess) {
            dispatch(getReservationDetail({booking_id: reservation.id}));
        }
        dispatch(clearBookingUpdateDates());
    };

    const handleChangeCheckin = (date) => {
        setCheckinDate(date);
        // Çıkış girişten sonra olmalı; kısalınca otomatik en az 1 gece bırak
        if (moment(checkoutDate).startOf('day').diff(moment(date).startOf('day'), 'days') < 1) {
            setCheckoutDate(moment(date).add(1, 'days').toDate());
        }
        // Giriş seçilince çıkış tarihi seçicisini otomatik aç (aynı tıklama kapatmasın diye ertele)
        setTimeout(() => setCheckoutOpen(true), 0);
    };

    const handleChangeCheckout = (date) => {
        setCheckoutDate(date);
    };

    const handleChangeTotal = ({target}) => {
        setTotal(target.value);
    };

    const onSubmit = () => {
        dispatch(bookingUpdateDates({
            booking_id: reservation.id,
            checkin_date: toApiDate(checkinDate),
            checkout_date: toApiDate(checkoutDate),
            total: Number(total),
        }));
    };

    const backendMessage = isDefined(updateError) && isDefined(updateError.response)
        ? updateError.response.data.message
        : null;

    return (
        <>
            <style>{`.room-rate-modal-save { margin-bottom: 0px !important; margin-top: 10px !important; }`}</style>
            <ButtonAtm
                className="filter-action-button float-right"
                startIcon={<i className="fas fa-calendar-alt"/>}
                buttontext={t('enw-reservation_update_dates-title', 'Tarih Güncelle')}
                fullwidth={true}
                onClick={handleClickOpen}
            />

            <Dialog onClose={handleClose} disableBackdropClick={true} aria-labelledby="booking-update-dates" open={open}
                    fullWidth maxWidth="sm">
                <DialogTitle className="red-bottom-border" id="booking-update-dates" onClose={handleClose}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 14, paddingRight: 32}}>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: 'rgba(215, 31, 38, 0.1)',
                            color: '#d71f26',
                            flexShrink: 0,
                        }}>
                            <i className="fas fa-calendar-alt" style={{fontSize: 20}}/>
                        </span>
                        <span style={{display: 'flex', flexDirection: 'column', lineHeight: 1.3}}>
                            <span style={{fontSize: 18, fontWeight: 700, color: '#1a1a1a'}}>
                                {t('enw-reservation_update_dates-title', 'Tarih Güncelle')}
                            </span>
                            {isDefined(reservation.booking_code) &&
                            <span style={{fontSize: 13, fontWeight: 500, color: '#8a8a8a'}}>
                                {reservation.booking_code}
                            </span>}
                        </span>
                    </div>
                </DialogTitle>
                <DialogContent>

                    {isSuccess ? (
                        <div className="text-center" style={{padding: '12px 4px 8px'}}>
                            <div style={{position: 'relative', display: 'inline-flex', marginTop: 6, marginBottom: 20}}>
                                <span className={classes.successRing} style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    borderRadius: '50%',
                                    border: '3px solid #43a047',
                                }}/>
                                <span className={classes.successBadge} style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 72,
                                    height: 72,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                    color: '#ffffff',
                                    boxShadow: '0 10px 26px rgba(67, 160, 71, 0.35)',
                                }}>
                                    <i className="fas fa-check" style={{fontSize: 30, marginTop: 6}}/>
                                </span>
                            </div>

                            <div className={classes.successBody}>
                                <Typography variant="h6" style={{fontWeight: 700, color: '#1a1a1a'}}>
                                    {t('enw-reservation_update_dates-success', 'Rezervasyon Tarihleri Güncellendi')}
                                </Typography>
                                <div style={{fontSize: 13, color: '#8a8a8a', marginTop: 4, marginBottom: 22}}>
                                    {updateData.data.booking_code || reservation.booking_code}
                                </div>

                                <div style={{
                                    border: '1px solid #eceef2',
                                    borderRadius: 14,
                                    padding: '18px 16px',
                                    background: '#fbfcfe',
                                    textAlign: 'left',
                                }}>
                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10}}>
                                        <div style={{flex: 1, minWidth: 0}}>
                                            <div style={{fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#a0a0a0'}}>
                                                {t('enw-input-start')}
                                            </div>
                                            <div style={{fontSize: 16, fontWeight: 700, color: '#1a1a1a'}}>
                                                {moment(updateData.data.checkin_date).format('DD.MM.YYYY')}
                                            </div>
                                        </div>
                                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0}}>
                                            <span style={{
                                                fontSize: 11, fontWeight: 700, color: '#1976d2',
                                                background: '#eaf3ff', border: '1px solid #cfe4fb',
                                                borderRadius: 20, padding: '2px 10px', whiteSpace: 'nowrap',
                                            }}>
                                                {updateData.data.nights} {t('enw-reservation_update_dates-per_night', 'gece')}
                                            </span>
                                            <i className="fas fa-arrow-right" style={{marginTop: 7, fontSize: 13, color: '#c3c9d4'}}/>
                                        </div>
                                        <div style={{flex: 1, minWidth: 0, textAlign: 'right'}}>
                                            <div style={{fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#a0a0a0'}}>
                                                {t('enw-input-end')}
                                            </div>
                                            <div style={{fontSize: 16, fontWeight: 700, color: '#1a1a1a'}}>
                                                {moment(updateData.data.checkout_date).format('DD.MM.YYYY')}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{height: 1, background: '#eceef2', margin: '14px 0'}}/>

                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10}}>
                                        <div>
                                            <div style={{fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#a0a0a0'}}>
                                                {t('enw-reservation-table-price')}
                                            </div>
                                            <div style={{fontSize: 20, fontWeight: 800, color: '#1a1a1a'}}>
                                                {updateData.data.total} {updateData.data.currency_code}
                                            </div>
                                        </div>
                                        {updateData.data.nights > 0 &&
                                        <span style={{
                                            fontSize: 12, fontWeight: 600, color: '#1976d2',
                                            background: '#eaf3ff', border: '1px solid #cfe4fb',
                                            borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap',
                                        }}>
                                            ≈ {(updateData.data.total / updateData.data.nights).toFixed(2)} {updateData.data.currency_code} / {t('enw-reservation_update_dates-per_night', 'gece')}
                                        </span>}
                                    </div>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    borderTop: '1px solid #f0f0f3',
                                    marginTop: 20,
                                    paddingTop: 18,
                                }}>
                                    <ButtonAtm
                                        className="m-auto room-rate-modal-save bg-red"
                                        buttontext={t('btn-close', 'Kapat')}
                                        color="primary"
                                        startIcon={<i className="fas fa-check"/>}
                                        onClick={handleClose}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <ValidatorForm onSubmit={onSubmit}>

                            <Alert severity="info" className="mb-16">
                                {t('enw-reservation_update_dates-info', 'Girilen tutar misafirin ödeyeceği nihai (net) toplamdır ve gece sayısına eşit bölünür. Kanal/OTA üzerinden gelen rezervasyonların tarihleri buradan değiştirilemez; kanal tarafından güncellenmelidir.')}
                            </Alert>

                            {isDefined(backendMessage) &&
                            <Alert severity="error" className="mb-16">{backendMessage}</Alert>}

                            <Grid container spacing={3}>
                                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={localeMap[getAppLang()]}>
                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            margin="normal"
                                            label={t('enw-input-start')}
                                            className="w-100"
                                            variant="inline"
                                            autoOk
                                            inputVariant="outlined"
                                            format="dd.MM.yyyy"
                                            value={checkinDate}
                                            onChange={handleChangeCheckin}
                                            KeyboardButtonProps={{'aria-label': 'change date'}}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small">
                                                            <TodayIcon/>
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <DatePicker
                                            margin="normal"
                                            label={t('enw-input-end')}
                                            className="w-100"
                                            variant="inline"
                                            autoOk
                                            inputVariant="outlined"
                                            format="dd.MM.yyyy"
                                            value={checkoutDate}
                                            minDate={moment(checkinDate).add(1, 'days')}
                                            onChange={handleChangeCheckout}
                                            open={checkoutOpen}
                                            onOpen={() => setCheckoutOpen(true)}
                                            onClose={() => setCheckoutOpen(false)}
                                            KeyboardButtonProps={{'aria-label': 'change date'}}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small">
                                                            <InsertInvitationIcon/>
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                </MuiPickersUtilsProvider>

                                <Grid item xs={12} md={6}>
                                    <TextValidator
                                        label={t('enw-reservation-table-price')}
                                        name="total"
                                        fullWidth
                                        autoComplete="off"
                                        value={total}
                                        type="number"
                                        onChange={handleChangeTotal}
                                        variant="outlined"
                                        validators={['required', 'minNumber:0']}
                                        errorMessages={[
                                            t("enw-notification_this_is_required"),
                                            t("enw-input-price_must_be_greater_than_zero")
                                        ]}
                                        InputProps={{
                                            endAdornment:
                                                <InputAdornment position="start">
                                                    {reservation.currency_code}
                                                </InputAdornment>
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <div style={{
                                        width: '100%',
                                        height: 56,
                                        boxSizing: 'border-box',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        background: 'linear-gradient(135deg, #eaf3ff 0%, #f7fbff 100%)',
                                        borderRadius: 10,
                                        padding: '0 12px',
                                        border: '1px solid #dcebfb',
                                        marginTop: 24,
                                    }}>
                                        <span style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 36,
                                            height: 36,
                                            borderRadius: 10,
                                            background: 'rgba(25, 118, 210, 0.12)',
                                            color: '#1976d2',
                                            flexShrink: 0,
                                        }}>
                                            <i className="fas fa-moon" style={{fontSize: 15}}/>
                                        </span>
                                        <div style={{display: 'flex', flexDirection: 'column', lineHeight: 1.15}}>
                                            <span style={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                letterSpacing: 0.4,
                                                textTransform: 'uppercase',
                                                color: '#a0a0a0',
                                            }}>
                                                {t('enw-reservation_update_dates-nights', 'Gece')}
                                            </span>
                                            <span style={{fontSize: 20, fontWeight: 700, color: '#1a1a1a'}}>
                                                {nights > 0 ? nights : 0}
                                                <span style={{fontSize: 12, fontWeight: 500, color: '#8a8a8a', marginLeft: 6}}>
                                                    {t('enw-reservation_update_dates-per_night', 'gece')}
                                                </span>
                                            </span>
                                        </div>
                                        {nights > 0 &&
                                        <span style={{
                                            marginLeft: 'auto',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: '#1976d2',
                                            background: '#eaf3ff',
                                            border: '1px solid #cfe4fb',
                                            borderRadius: 20,
                                            padding: '4px 10px',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            ≈ {perNight.toFixed(2)} {reservation.currency_code} / {t('enw-reservation_update_dates-per_night', 'gece')}
                                        </span>}
                                    </div>
                                </Grid>

                                <ColAtm className="mt-13 mb-12" md={12}>
                                    <ButtonAtm
                                        className="m-auto room-rate-modal-save bg-red"
                                        type="submit"
                                        buttontext={t('btn-save')}
                                        color="primary"
                                        disabled={updateFetching || nights < 1}
                                    />
                                </ColAtm>
                            </Grid>
                        </ValidatorForm>
                    )}

                    {updateFetching &&
                    <Backdrop className={classes.backdrop} open>
                        <CircularProgress color="inherit"/>
                    </Backdrop>}

                </DialogContent>
            </Dialog>
        </>
    );
};

export default BookingUpdateDatesModal;
